import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { toast } from 'react-hot-toast';

export default function AdjustTest() {
  // Fixed dimensions required by user
  const fixedDimensions = 'day,channel,os_name,platform,partner,campaign,adgroup,source_network,creative';
  const [dimensions, setDimensions] = useState(fixedDimensions);
  const [metrics, setMetrics] = useState('installs,sessions,revenue,purchase_events,all_revenue');
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  const [utcOffset, setUtcOffset] = useState('+03:00');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [eventsRows, setEventsRows] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventFilter, setEventFilter] = useState('purchase');
  const [eventParamsLoading, setEventParamsLoading] = useState(false);
  const [eventParamsResult, setEventParamsResult] = useState(null);

  // Column configuration: order and human-readable labels
  const dimensionColumns = [
    { key: 'day', label: 'Date' },
    { key: 'channel', label: 'Channel' },
    { key: 'os_name', label: 'OS name' },
    { key: 'platform', label: 'Platform' },
    { key: 'partner', label: 'Partner' },
    { key: 'campaign', label: 'Campaign name' },
    { key: 'adgroup', label: 'Adgroup name' }, // hierarchical: after campaign
    { key: 'source_network', label: 'Source name' },
    { key: 'creative', label: 'Ad name' }
  ];
  const metricColumns = [
    { key: 'installs', label: 'Installs' },
    { key: 'sessions', label: 'Sessions' },
    { key: 'revenue', label: 'IAP Revenue' },
    { key: 'purchase_events', label: 'Purchase' },
    { key: 'all_revenue', label: 'All revenue' }
  ];

  const fetchReport = async () => {
    try {
      setLoading(true);
      setRows([]);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      const params = new URLSearchParams({
        dimensions,
        metrics,
        date_period: `${startDate}:${endDate}`,
        ad_spend_mode: 'network',
        attribution_source: 'dynamic',
        utc_offset: utcOffset,
        format_dates: 'true'
      });
      const res = await fetch(`https://api.glowytics.com/api/auth/adjust/report?${params.toString()}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        const err = ct.includes('application/json') ? await res.json().catch(() => ({})) : await res.text();
        throw new Error((err && (err.error || err.details)) || String(err) || `HTTP ${res.status}`);
      }
      const data = ct.includes('application/json') ? await res.json() : {};
      const list = Array.isArray(data.rows) ? data.rows : (Array.isArray(data.data) ? data.data : []);
      setRows(list);
      toast.success(`Fetched ${list.length} rows`);
    } catch (e) {
      console.error('Adjust report fetch error:', e);
      toast.error(e.message || 'Failed to fetch Adjust report');
    } finally {
      setLoading(false);
    }

    // Fetch events summary just after
    try {
      setEventsLoading(true);
      setEventsRows([]);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;
      const params = new URLSearchParams({
        date_period: `${startDate}:${endDate}`,
        utc_offset: utcOffset
      });
      const res = await fetch(`https://api.glowytics.com/api/auth/adjust/events-summary?${params.toString()}`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        const err = ct.includes('application/json') ? await res.json().catch(() => ({})) : await res.text();
        throw new Error((err && (err.error || err.details)) || String(err) || `HTTP ${res.status}`);
      }
      const data = ct.includes('application/json') ? await res.json() : {};
      const list = Array.isArray(data.rows) ? data.rows : [];
      setEventsRows(list);
    } catch (e) {
      console.error('Adjust events summary fetch error:', e);
      // show non-blocking error
    } finally {
      setEventsLoading(false);
    }
  };

  const fetchEventParams = async () => {
    try {
      setEventParamsLoading(true);
      setEventParamsResult(null);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        toast.error('Please select a brand first');
        return;
      }
      const params = new URLSearchParams();
      if (eventFilter && eventFilter.trim()) {
        params.set('event_id', eventFilter.trim());
      }
      const res = await fetch(
        `https://api.glowytics.com/api/auth/adjust/event-params?${params.toString()}`,
        {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId }
        }
      );
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) {
        const err = ct.includes('application/json')
          ? await res.json().catch(() => ({}))
          : await res.text();
        throw new Error(
          (err && (err.error || err.details)) || String(err) || `HTTP ${res.status}`
        );
      }
      const data = ct.includes('application/json') ? await res.json() : {};
      setEventParamsResult(data);
      toast.success(`Fetched ${Array.isArray(data?.events) ? data.events.length : 0} event(s)`);
    } catch (e) {
      console.error('Adjust event params fetch error:', e);
      toast.error(e.message || 'Failed to fetch event params');
    } finally {
      setEventParamsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h2 className="text-xl font-semibold mb-4">Adjust Test Report</h2>
      <div className="bg-white rounded-xl border p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dimensions</label>
            <input
              type="text"
              value={dimensions}
              onChange={(e) => setDimensions(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg bg-gray-50 text-gray-700"
              readOnly
            />
            <p className="text-xs text-gray-500 mt-1">Dimensions are fixed for this view.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Metrics</label>
            <input
              type="text"
              value={metrics}
              onChange={(e) => setMetrics(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              max={endDate}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              min={startDate}
              max={new Date().toISOString().slice(0,10)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">UTC Offset</label>
            <input
              type="text"
              value={utcOffset}
              onChange={(e) => setUtcOffset(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
              placeholder="+03:00"
            />
          </div>
        </div>
        <div className="flex justify-end">
          <Button onClick={fetchReport} className="bg-blue-600 text-white hover:bg-blue-700 rounded-xl px-4" disabled={loading}>
            {loading ? 'Fetching…' : 'Fetch'}
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border p-6 mt-6">
        <h3 className="text-lg font-medium mb-3">Events summary</h3>
        {eventsLoading ? (
          <p className="text-sm text-gray-500">Loading events…</p>
        ) : eventsRows.length === 0 ? (
          <p className="text-sm text-gray-500">No events</p>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left p-2 border-b">OS name</th>
                  <th className="text-left p-2 border-b">Event name</th>
                  <th className="text-left p-2 border-b">Event slug</th>
                  <th className="text-left p-2 border-b">Tokens</th>
                  <th className="text-right p-2 border-b">Events</th>
                  <th className="text-right p-2 border-b">Event value</th>
                </tr>
              </thead>
              <tbody>
                {eventsRows.slice(0, 300).map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                    <td className="p-2 border-b">{r.os_name}</td>
                    <td className="p-2 border-b">{r.event_name}</td>
                    <td className="p-2 border-b">{r.event_id}</td>
                    <td className="p-2 border-b">{Array.isArray(r.tokens) ? r.tokens.join(', ') : ''}</td>
                    <td className="p-2 border-b text-right">{r.events ?? ''}</td>
                    <td className="p-2 border-b text-right">{r.revenue ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border p-6 mt-6">
        <h3 className="text-lg font-medium mb-3">Event parameters (debug)</h3>
        <div className="flex flex-wrap items-end gap-3 mb-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event ID filter (e.g. purchase)
            </label>
            <input
              type="text"
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="w-64 px-3 py-2 border rounded-lg"
              placeholder="purchase"
            />
          </div>
          <Button
            onClick={fetchEventParams}
            className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-xl px-4"
            disabled={eventParamsLoading}
          >
            {eventParamsLoading ? 'Fetching…' : 'Fetch event params'}
          </Button>
        </div>
        {eventParamsLoading ? (
          <p className="text-sm text-gray-500">Loading event params…</p>
        ) : !eventParamsResult ? (
          <p className="text-sm text-gray-500">
            No event params loaded yet. Click &quot;Fetch event params&quot; to query Adjust
            events endpoint.
          </p>
        ) : Array.isArray(eventParamsResult.events) &&
          eventParamsResult.events.length > 0 ? (
          <div className="mt-3">
            <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-3 overflow-x-auto max-h-80">
{JSON.stringify(eventParamsResult.events, null, 2)}
            </pre>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No matching events found for this filter.</p>
        )}
      </div>

      <div className="bg-white rounded-xl border p-6 mt-6">
        <h3 className="text-lg font-medium mb-3">Results</h3>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500">No data</p>
        ) : (
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr>
                  {dimensionColumns.map((col) => (
                    <th key={col.key} className="text-left p-2 border-b">{col.label}</th>
                  ))}
                  {metricColumns.map((col) => (
                    <th key={col.key} className="text-right p-2 border-b">{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 200).map((r, i) => (
                  <tr key={i} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
                    {dimensionColumns.map((col) => (
                      <td key={col.key} className="p-2 border-b">{r[col.key] != null ? String(r[col.key]) : ''}</td>
                    ))}
                    {metricColumns.map((col) => (
                      <td key={col.key} className="p-2 border-b text-right">{r[col.key] != null ? String(r[col.key]) : ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 200 && (
              <p className="text-xs text-gray-500 mt-2">Showing first 200 rows</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}


